/**
 * Stories page — post a photo story that mentions users from the scraped pool.
 *
 * One-shot tool (not a campaign): pick an account, upload a photo, choose how
 * many pool users to tag, write a caption, post. Mass-tagging strangers reads
 * as spam, so the page warns about ban risk and caps mentions.
 *
 * Route: /app/stories
 */

import { motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ImagePlus,
  Send,
  Sparkles,
  Users,
  XCircle,
} from "lucide-react";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { Button, Card, Input } from "@/design";
import { useAccounts } from "@/api/accounts";
import { usePool } from "@/api/outreach";
import { uploadStoryMedia, useStories, usePostStory } from "@/api/stories";
import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";

export function StoriesPage() {
  const navigate = useNavigate();
  const { data: accounts } = useAccounts();
  const { data: pool } = usePool();
  const { data: history } = useStories();
  const post = usePostStory();
  const fileRef = useRef<HTMLInputElement>(null);

  const [accountId, setAccountId] = useState<number | null>(null);
  const [mediaPath, setMediaPath] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [mentionCount, setMentionCount] = useState(10);
  const [uploading, setUploading] = useState(false);

  const poolTotal = pool?.total ?? 0;
  const maxMentions = Math.min(30, poolTotal);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadStoryMedia(file);
      setMediaPath(res.path);
      setPreviewUrl(URL.createObjectURL(file));
      haptic.notify("success");
    } catch (err) {
      toast.error((err as Error)?.message || "Не удалось загрузить фото");
      haptic.notify("error");
    } finally {
      setUploading(false);
    }
  };

  const handlePost = async () => {
    if (accountId == null) {
      toast.error("Выберите аккаунт");
      return;
    }
    if (!mediaPath) {
      toast.error("Загрузите фото");
      return;
    }
    haptic.impact("medium");
    try {
      const res = await post.mutateAsync({
        account_id: accountId,
        media_path: mediaPath,
        caption,
        mention_count: mentionCount,
      });
      haptic.notify("success");
      toast.success(`История опубликована · отмечено ${res.mentioned_count}`);
      setMediaPath(null);
      setPreviewUrl(null);
      setCaption("");
    } catch (e) {
      toast.error((e as Error)?.message || "Не удалось опубликовать");
      haptic.notify("error");
    }
  };

  return (
    <div className="px-5 pt-4 pb-safe-nav space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => {
            haptic.select();
            navigate("/app");
          }}
          className="size-10 rounded-full glass grid place-items-center hover:bg-white/[0.06] transition-colors"
          aria-label="Назад"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="text-[15px] font-semibold tracking-tight">Истории</div>
        <div className="size-10" />
      </div>

      {/* Ban-risk warning */}
      <div className="flex items-start gap-2.5 rounded-2xl bg-warning/[0.08] border border-warning/20 px-4 py-3">
        <AlertTriangle className="size-4 text-warning shrink-0 mt-0.5" />
        <div className="text-[12px] text-text-muted leading-relaxed">
          Массовые отметки незнакомых людей в историях могут восприниматься как
          спам и привести к блокировке аккаунта. Отмечайте умеренно и по своей
          сетке лидов.
        </div>
      </div>

      {/* Composer */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <Card padded={false} className="p-5 space-y-4">
          {/* Photo */}
          <button
            onClick={() => fileRef.current?.click()}
            className={cn(
              "relative w-full aspect-[9/16] max-h-[280px] rounded-2xl overflow-hidden",
              "border border-white/[0.08] grid place-items-center transition-colors",
              previewUrl ? "" : "bg-white/[0.03] hover:bg-white/[0.05]",
            )}
          >
            {previewUrl ? (
              <img src={previewUrl} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <div className="flex flex-col items-center gap-2 text-text-muted">
                <ImagePlus className="size-8" />
                <span className="text-[13px]">{uploading ? "Загрузка…" : "Добавить фото"}</span>
              </div>
            )}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
          />

          {/* Account */}
          <Field label="Аккаунт">
            <div className="flex flex-wrap gap-2">
              {(accounts ?? [])
                .filter((a) => !a.is_banned)
                .map((a) => (
                  <Chip
                    key={a.id}
                    active={accountId === a.id}
                    onClick={() => { haptic.select(); setAccountId(a.id); }}
                  >
                    {a.name}
                  </Chip>
                ))}
            </div>
          </Field>

          {/* Caption */}
          <Field label="Подпись">
            <Input
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Текст истории (необязательно)"
            />
          </Field>

          {/* Mentions */}
          <Field label={`Отметить юзеров из пула: ${mentionCount}`}>
            {poolTotal === 0 ? (
              <div className="text-[12px] text-text-muted">
                Пул пуст. Спарсите участников на вкладке «Парсинг + ЛС», чтобы
                отмечать их в историях.
              </div>
            ) : (
              <input
                type="range"
                min={0}
                max={maxMentions}
                value={mentionCount}
                onChange={(e) => setMentionCount(Number(e.target.value))}
                className="w-full accent-accent"
              />
            )}
          </Field>

          <Button
            fullWidth
            size="lg"
            leftIcon={<Sparkles />}
            onClick={handlePost}
            loading={post.isPending}
            disabled={uploading}
          >
            Опубликовать историю
          </Button>
        </Card>
      </motion.div>

      {/* History */}
      <div className="space-y-2">
        <div className="text-xs text-text-muted uppercase tracking-wider font-medium px-1 flex items-center gap-1.5">
          <Send className="size-3" />
          Опубликованные
        </div>
        {(history?.length ?? 0) === 0 ? (
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-4 py-8 text-center text-xs text-text-muted">
            Пока нет опубликованных историй
          </div>
        ) : (
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] divide-y divide-white/[0.04]">
            {history!.map((h) => {
              const ok = h.status === "posted";
              return (
                <div key={h.id} className="px-4 py-2.5 flex items-center gap-3">
                  <div
                    className={cn(
                      "size-8 rounded-lg grid place-items-center shrink-0",
                      ok ? "bg-success/15 text-success" : "bg-danger/15 text-danger",
                    )}
                  >
                    {ok ? <CheckCircle2 className="size-3.5" /> : <XCircle className="size-3.5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">
                      {h.caption || "История без подписи"}
                    </div>
                    <div className="text-[11px] text-text-muted truncate flex items-center gap-1">
                      <Users className="size-3" />
                      {ok ? `отмечено ${h.mentioned_count}` : h.error_text ?? "ошибка"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-text-muted font-medium px-1">{label}</div>
      {children}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-3 py-2 rounded-xl text-[13px] font-medium transition-colors",
        active
          ? "bg-gradient-accent text-white"
          : "bg-white/[0.04] text-text-muted hover:bg-white/[0.07]",
      )}
    >
      {children}
    </button>
  );
}
